import React, { useState } from 'react';
import { Bell, AlertTriangle, Zap, Activity, Newspaper, Target, TrendingUp, TrendingDown, ShieldAlert, Lightbulb, ChevronRight } from 'lucide-react';
import { VerdictChip } from './Verdict';

const typeIcon = {
    volume_spike: Zap,
    funding_anomaly: Activity,
    oi_break: Activity,
    pump_probability: TrendingUp,
    dump_risk: TrendingDown,
    pump_done: TrendingUp,
    dump_done: TrendingDown,
    impulse: Zap,
    news_fake_pump: Newspaper,
    news_fake_dump: Newspaper,
    news_confirmed_up: Newspaper,
    news_confirmed_down: Newspaper,
    level_tp1: Target,
    level_tp2: Target,
    level_stop: ShieldAlert,
    position_adverse: ShieldAlert,
};

const typeLabel = {
    volume_spike: 'Hacim Patlaması',
    funding_anomaly: 'Funding Anomalisi',
    oi_break: 'Pozisyon Değişimi',
    pump_probability: 'YUKARI Patlama Olasılığı',
    dump_risk: 'AŞAĞI Düşme Riski',
    pump_done: '🚂 Patladı — Tren Kaçtı',
    dump_done: '🚂 Düştü — SHORT İçin Geç',
    impulse: '🚀 İlk Hareket',
    news_fake_pump: '📰 Haber — Yanıltıcı',
    news_fake_dump: '📰 Haber — Yanıltıcı',
    news_confirmed_up: '📰 Haber Doğrulandı',
    news_confirmed_down: '📰 Haber Doğrulandı',
    level_tp1: '🎯 TP1 Seviyesi Vuruldu',
    level_tp2: '🎯 TP2 Seviyesi Vuruldu',
    level_stop: '⛔ Stop Seviyesi Vuruldu',
    position_adverse: '⚠ Pozisyon Ters Gidiyor',
};

const sevColor = {
    green: 'border-emerald-400/30 bg-emerald-400/5 text-emerald-300',
    yellow: 'border-amber-400/30 bg-amber-400/5 text-amber-300',
    red: 'border-rose-500/30 bg-rose-500/5 text-rose-300',
};

const verdictColor = (verdict) => {
    if (!verdict) return null;
    const v = verdict.toUpperCase();
    if (v.includes('LONG GİR') || v.includes('KÜÇÜK LONG') || v.includes('LONG HAZIRLAN'))
        return 'bg-emerald-500/20 text-emerald-100 border-emerald-400/50';
    if (v.includes('SHORT GİR') || v.includes('KÜÇÜK SHORT'))
        return 'bg-rose-500/20 text-rose-100 border-rose-400/50';
    if (v.includes('AÇMA') || v.includes('KAPAT') || v.includes('ÇIK') || v.includes('KAÇIN'))
        return 'bg-rose-500/15 text-rose-200 border-rose-500/40';
    if (v.includes('KÂRI AL') || v.includes('YARI KAPAT'))
        return 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40';
    if (v.includes('BEKLE'))
        return 'bg-amber-500/15 text-amber-200 border-amber-500/40';
    return 'bg-slate-700 text-slate-200 border-slate-600';
};

/**
 * Alerts panel — each alert row shows:
 *   1. Symbol + severity-colored type pill + big VERDICT chip (GİR / BEKLE / KAPAT / ...)
 *   2. One-line technical message (what happened)
 *   3. Expandable "Ne demek?" + "Ne yapmalıyım?" explanation
 */
export default function AlertsPanel({ alerts = [], onSelect, verdicts = {} }) {
    const [expanded, setExpanded] = useState({}); // key -> bool
    const toggle = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));

    return (
        <div className="border border-slate-700 bg-slate-800 rounded-md overflow-hidden h-full flex flex-col" data-testid="alerts-panel">
            <div className="px-5 py-4 border-b border-slate-700 flex items-center gap-2 flex-wrap">
                <Bell className="h-4 w-4 text-slate-300" />
                <h3 className="text-sm font-semibold tracking-tight text-slate-100">Alarmlar</h3>
                <span className="text-[10px] text-slate-400">— her alarmın yanında coinin TEK KARARI yazar (Adaylar listesiyle aynı)</span>
                <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400">
                    {alerts.length} aktif · tıkla, detay aç
                </span>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-slate-700/50">
                {alerts.length === 0 ? (
                    <div className="p-5 text-xs text-slate-400">
                        Şu anda aktif alarm yok. Tarama yenilendikçe burada görünecek.
                    </div>
                ) : (
                    alerts.slice(0, 30).map((a, i) => {
                        const Icon = typeIcon[a.type] || Bell;
                        const stableKey = `${a.type}-${a.symbol}-${a.message?.slice(0, 30) || i}`;
                        const isExpanded = expanded[stableKey];
                        const label = typeLabel[a.type] || a.type.replace(/_/g, ' ').toUpperCase();
                        const hasDetails = !!(a.meaning || a.action);
                        return (
                            <div
                                key={stableKey}
                                data-testid={`alert-item-${i}`}
                                className="px-4 py-2.5 hover:bg-slate-700/40 transition-colors"
                            >
                                <div className="flex items-start gap-3">
                                    <span className={`shrink-0 h-6 w-6 rounded-sm border flex items-center justify-center ${sevColor[a.severity] || sevColor.yellow}`}>
                                        <Icon className="h-3 w-3" />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <button
                                                onClick={() => onSelect?.(a.symbol)}
                                                data-testid={`alert-coin-${i}`}
                                                className="text-xs font-mono font-semibold text-slate-100 hover:text-emerald-300"
                                            >
                                                {a.symbol?.replace('USDT', '') || '—'}
                                            </button>
                                            {verdicts[a.symbol] ? (
                                                <span className="inline-flex items-center gap-1" data-testid={`alert-verdict-${i}`} data-action={verdicts[a.symbol].action}>
                                                    <span className="text-[9px] uppercase tracking-wider text-slate-500">Karar</span>
                                                    <VerdictChip v={verdicts[a.symbol]} withDir />
                                                </span>
                                            ) : a.verdict && (
                                                <span
                                                    data-testid={`alert-verdict-${i}`}
                                                    className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${verdictColor(a.verdict)}`}
                                                >
                                                    {a.verdict}
                                                </span>
                                            )}
                                            <span className="text-[10px] font-medium uppercase tracking-wider text-slate-400">
                                                {label}
                                            </span>
                                            {hasDetails && (
                                                <button
                                                    onClick={() => toggle(stableKey)}
                                                    data-testid={`alert-expand-${i}`}
                                                    className="ml-auto flex items-center gap-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-300 hover:text-emerald-200"
                                                >
                                                    {isExpanded ? 'Kapat' : 'Ne demek?'}
                                                    <ChevronRight className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                </button>
                                            )}
                                        </div>
                                        <div className="text-[11px] text-slate-300 mt-0.5 leading-snug">
                                            {a.message}
                                        </div>

                                        {hasDetails && isExpanded && (
                                            <div className="mt-2 space-y-2">
                                                {a.meaning && (
                                                    <div className="bg-slate-900/50 border border-slate-700 rounded-sm px-2.5 py-1.5">
                                                        <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">
                                                            <Lightbulb className="h-3 w-3" />
                                                            Ne Demek?
                                                        </div>
                                                        <div className="text-[11px] text-slate-200 leading-relaxed">
                                                            {a.meaning}
                                                        </div>
                                                    </div>
                                                )}
                                                {a.action && (
                                                    <div className="bg-emerald-500/10 border border-emerald-500/25 rounded-sm px-2.5 py-1.5">
                                                        <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-emerald-300 mb-0.5">
                                                            <Target className="h-3 w-3" />
                                                            Ne Yapmalıyım?
                                                        </div>
                                                        <div className="text-[11px] text-slate-100 leading-relaxed">
                                                            {a.action}
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="text-[9px] text-slate-400 italic">
                                                    ⚠ Yatırım tavsiyesi değildir — kendi riskini kendin yönet.
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
